// Endpoints do catálogo normalizado por loja (/v1/stores/:storeId/catalog),
// injetados na API base. Tag 'StoreCatalog' registrada em lib/api. O export é um
// download binário (Content-Disposition) — foge do fluxo JSON do RTK Query, então
// vai por fetch autenticado + blob (padrão de download do repo).
import { api } from '@/lib/api';
import { getToken } from '@/lib/auth/token-storage';
import type {
  CatalogCategoryNode,
  CatalogFull,
  CatalogImportSummary,
  CatalogProductDetail,
  UpdateCatalogProductBody,
} from './storeCatalog.model';

export const storeCatalogApi = api.injectEndpoints({
  endpoints: (build) => ({
    getStoreCatalog: build.query<CatalogCategoryNode[], string>({
      query: (storeId) => `/stores/${storeId}/catalog`,
      providesTags: (_result, _err, storeId) => [{ type: 'StoreCatalog', id: storeId }],
    }),

    importStoreCatalog: build.mutation<CatalogImportSummary, { storeId: string; file: File }>({
      query: ({ storeId, file }) => {
        // FormData: o baseQuery detecta e deixa o browser setar o Content-Type com o
        // boundary correto (baseQuery.ts). Campo `file` = o que o controller espera.
        const formData = new FormData();
        formData.append('file', file);
        return { url: `/stores/${storeId}/catalog/import`, method: 'POST', body: formData };
      },
      invalidatesTags: (_result, _err, { storeId }) => [{ type: 'StoreCatalog', id: storeId }],
    }),

    // Detalhe de um produto (medidas, variantes, faixas) — abre o modal de edição.
    // Tag por produto (`storeId:productId`) pra o PATCH conseguir invalidar só ele.
    getCatalogProduct: build.query<CatalogProductDetail, { storeId: string; productId: string }>({
      query: ({ storeId, productId }) => `/stores/${storeId}/catalog/products/${productId}`,
      providesTags: (_result, _err, { storeId, productId }) => [
        { type: 'StoreCatalog', id: `${storeId}:${productId}` },
      ],
    }),

    // Edita um produto do catálogo. Invalida a tag da loja (recarrega árvore +
    // contagens no CatalogSection) e a tag do próprio produto (o detalhe).
    updateCatalogProduct: build.mutation<
      CatalogProductDetail,
      { storeId: string; productId: string; body: UpdateCatalogProductBody }
    >({
      query: ({ storeId, productId, body }) => ({
        url: `/stores/${storeId}/catalog/products/${productId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _err, { storeId, productId }) => [
        { type: 'StoreCatalog', id: storeId },
        { type: 'StoreCatalog', id: `${storeId}:${productId}` },
      ],
    }),

    // Visão consolidada só-consulta do catálogo da loja (RF-04, painel da rede).
    // Mesma tag da árvore — recarrega junto com contagens quando algo muda.
    getStoreCatalogFull: build.query<CatalogFull, string>({
      query: (storeId) => `/stores/${storeId}/catalog/full`,
      providesTags: (_result, _err, storeId) => [{ type: 'StoreCatalog', id: storeId }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetStoreCatalogQuery,
  useImportStoreCatalogMutation,
  useGetCatalogProductQuery,
  useUpdateCatalogProductMutation,
  useGetStoreCatalogFullQuery,
} = storeCatalogApi;

/**
 * Baixa o .xlsx do catálogo da loja (modelo preenchido / round-trip). Fora do RTK
 * porque a resposta é binária: fetch com Bearer + blob + link temporário.
 * baseUrl do app = /api/v1 (baseQuery.ts).
 */
export async function downloadStoreCatalogXlsx(storeId: string): Promise<void> {
  const token = getToken();
  const res = await fetch(`/api/v1/stores/${storeId}/catalog/export`, {
    credentials: 'include',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    throw new Error(`Falha ao baixar a planilha (${res.status}).`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `catalogo-${storeId}.xlsx`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
